import { Inject, Injectable } from '@angular/core';
import {
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigConstants,
    ConfigInstanceToken,
    ConfigInterface,
    EncodingUtilities,
    ENDPOINTS,
    isDefined,
    Notification,
    OnlineOnlyService,
    SynchronizationInstanceToken,
    SynchronizationInterface,
    UriUtilitiesService,
    UserMasterInstanceToken,
    UserMasterInterface,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import moment from 'moment';
import { Observable, Observer, of, throwError } from 'rxjs';
import { catchError, mergeMap, switchMap } from 'rxjs/operators';
import { DocumentStateUpload } from '../store/state/document.state';
import {
    DocBoxListModel,
    DocBoxResponseStaus,
    ODocBoxUploadModel,
    ODocBoxUploadResponse,
} from './docBox-odata.interface';
import { OdataDocumentsModel } from './document-odata.interface';
import { OdataFormsService } from './odata-forms.service';

@Injectable({
    providedIn: 'root',
})
export class DocBoxService {
    public constructor(
        @Inject(ClientManagerInstanceToken) protected readonly clientManager: ClientManagerInterface,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        @Inject(SynchronizationInstanceToken) protected readonly synchronizationService: SynchronizationInterface,
        private readonly uriUtilities: UriUtilitiesService,
        private readonly onlineOnlyService: OnlineOnlyService,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
        @Inject(UserMasterInstanceToken) private readonly userMasterService: UserMasterInterface,
        private readonly odataFormsService: OdataFormsService,
    ) {}

    public getDocBoxFiles(
        materialNumber: string,
        serialNumber: string,
        technicalIdentNo?: string,
        functionalLocation?: string,
    ): Observable<DocBoxListModel[]> {
        let query = ENDPOINTS.GetDocBoxListSet.toString();
        let filter = '';
        if (technicalIdentNo) {
            filter = `TechnicalIdent eq '${technicalIdentNo}'`;
        } else if (functionalLocation) {
            filter = `Funcloc eq '${functionalLocation}'`;
        } else if (materialNumber && serialNumber) {
            filter = `Material eq '${materialNumber}' and SerialNo eq '${serialNumber}'`;
        }
        query = this.uriUtilities.appendFilterValue(query, filter);
        return this.onlineOnlyService.get(query) as Observable<DocBoxListModel[]>;
    }

    public getDocument(docBoxId: string[]): Observable<OdataDocumentsModel[]> {
        let query = ENDPOINTS.GetDocBoxDocumentsSet.toString();
        const filter = `(${docBoxId.map((id) => `DocBoxId eq '${id}'`).join(' or ')})`;
        const format = '$format=json';

        query = this.uriUtilities.appendFilterValue(query, filter);
        query = this.uriUtilities.appendParameter(query, format);

        return this.onlineOnlyService.get(query) as Observable<OdataDocumentsModel[]>;
    }

    public saveDocument(data: OdataDocumentsModel): void {
        const blob = EncodingUtilities.base64DecodeBlob(data.Documents, 'application/zip');
        const url = URL.createObjectURL(blob);
        const zipFile = document.createElement('a');
        zipFile.href = url;
        zipFile.download = data.ZipArchiveName;
        zipFile.click();
    }

    public uploadDocBoxFile(document: DocumentStateUpload, notification: Notification): Observable<void> {
        if (!isDefined(document.file)) {
            return throwError(() => 'No file to be uploaded');
        }
        const file = document.file;

        return this.readFile(document.file).pipe(
            switchMap((base64) => {
                const materialNumber = notification.equipmentSet[0].matnr;
                const serialNumber = notification.equipmentSet[0].sernr;
                const user = this.userMasterService.getSelectedUser();

                const oDataUploadModel: ODocBoxUploadModel = {
                    Attachment: base64,
                    ContentType: file.type, // is d efinitely available because of check in method start
                    DocumentType: document.docType,
                    FileName: file.name, // is d efinitely available because of check in method start
                    MaterialNumber: materialNumber,
                    SerialNumber: serialNumber,
                    Name: document.title,
                    ProtectionClass: document.protectionClass,
                    Printno: document.printNumber,
                    DocumentState: document.reportStatus,
                    SourceSystem: this.configService.getBackendName(),
                    Salesorg: user.salesOrg,
                    Plant: user.plant,
                    Country: user.country,
                    Notification: notification.identifier.qmNum,
                    NotificationType: notification.notificationType,
                    Orderno: document.orderNumber,
                    Funcloc: document.funcLoc,
                    EquipmentNumber: document.equipment,
                    StartDate: moment(document.date).utcOffset(0, true).format(ConfigConstants.formatSAPDate),
                };

                if (isDefined(document.reportType)) {
                    oDataUploadModel.ReportType = document.reportType;
                }

                if (document.paperworkUpload && document.taskNumber && document.sapID) {
                    oDataUploadModel.PaperworkUpload = ConfigConstants.sapTrue;
                    oDataUploadModel.TaskNumber = document.taskNumber;
                    oDataUploadModel.SapID = document.sapID;
                }

                return this.postRequest(ENDPOINTS.DocboxUploadSet, oDataUploadModel).pipe(
                    catchError((error) => {
                        const stringifiedError = JSON.stringify(error);
                        this.log.error(
                            `DocBoxService: uploadDocBoxFile: uploading file failed, error "${stringifiedError}"`,
                        );
                        return throwError(() => stringifiedError);
                    }),
                );
            }),
        );
    }
    private readFile(file: File): Observable<string> {
        const reader = new FileReader();

        return new Observable((observer: Observer<string>) => {
            reader.readAsDataURL(file);
            reader.addEventListener('load', () => {
                observer.next(typeof reader.result === 'string' ? reader.result.replace(/.*;base64,/, '') : '');
                observer.complete();
            });
            reader.addEventListener('error', (event) => {
                observer.error(event);
            });
        });
    }

    protected postRequest(url: string, payload: ODocBoxUploadModel): Observable<void> {
        if (this.userMasterService.isPaceOnlineUser() || this.clientManager.isPaceFormsFI()) {
            return this.onlineOnlyService.post(url, payload).pipe(
                switchMap((response: ODocBoxUploadResponse) => {
                    if (response.rStatusText === DocBoxResponseStaus.Rejected) {
                        return throwError(() => response.rMessage);
                    }
                    return of(undefined);
                }),
            );
        }

        // TODO: for deep insert requests we currently don't have a response that will give us the rejected status from a docbox upload.
        return this.odataFormsService
            .deepInsertRequest(url, payload, payload.Name)
            .pipe(mergeMap(() => this.synchronizationService.sendOfflineData()));
    }
}
